import type { JobSummaryDTO } from '@devrobot/shared';
import { Link } from 'react-router';
import { useNow } from '../hooks/useNow';
import { durationBetween, formatDuration, shortSha } from '../lib/format';
import { EmptyState } from './EmptyState';
import { RelativeTime } from './RelativeTime';
import { StatusBadge } from './StatusBadge';
import { ListChecks } from 'lucide-react';

export function SourceBadge({ source }: { source: JobSummaryDTO['source'] }) {
  const label = source === 'ui' ? 'UI' : source === 'cli' ? 'CLI' : 'system';
  return <span className="badge badge-ghost badge-xs">{label}</span>;
}

const isActive = (j: JobSummaryDTO) => !['succeeded', 'failed', 'canceled'].includes(j.state);

function Where({ j }: { j: JobSummaryDTO }) {
  return (
    <>
      {j.project ? (
        <Link className="link link-hover" to={`/projects/${j.project.key}`}>
          {j.project.key}
        </Link>
      ) : null}
      {j.environment ? <span className="text-base-content/65"> / {j.environment}</span> : null}
    </>
  );
}

/**
 * Jobs as a table (md and up) or a stacked list (phones).
 * `variant="inset"` (default) suits a table inside a Section card; `variant="card"` gives it its own raised surface.
 */
export function JobsTable({
  jobs,
  emptyTitle = 'No jobs yet',
  emptyText,
  showProject = true,
  variant = 'inset',
}: {
  jobs: JobSummaryDTO[];
  emptyTitle?: string;
  emptyText?: string;
  showProject?: boolean;
  variant?: 'inset' | 'card';
}) {
  const now = useNow();
  if (jobs.length === 0) return <EmptyState icon={ListChecks} title={emptyTitle}>{emptyText}</EmptyState>;
  const frame = variant === 'card' ? 'card dr-surface overflow-hidden' : 'overflow-hidden rounded-field border border-hairline';
  return (
    <div className={`min-w-0 ${frame}`}>
      <ul className="divide-y divide-hairline md:hidden" aria-label="Jobs">
        {jobs.map((j) => (
          <li key={j.id} className="flex flex-col gap-1.5 px-3 py-3">
            <div className="flex items-start justify-between gap-3">
              <Link to={`/jobs/${j.id}`} className="min-w-0 font-semibold break-anywhere hover:underline">
                {j.summary}
              </Link>
              <StatusBadge kind="job" value={j.state} size="xs" className="mt-0.5 shrink-0" />
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-base-content/60">
              <span className="badge badge-ghost badge-xs">{j.kind}</span>
              {showProject && (j.project || j.environment) ? (
                <span>
                  <Where j={j} />
                </span>
              ) : null}
              {showProject && j.vps ? <span>on {j.vps.name}</span> : null}
              <span aria-hidden="true">·</span>
              <RelativeTime value={j.createdAt} />
              {j.stage && isActive(j) ? <span>· {j.stage}</span> : null}
            </div>
          </li>
        ))}
      </ul>
      <div className="overflow-x-auto max-md:hidden">
        <table className="table table-sm">
          <thead>
            <tr className="text-xs tracking-wide text-base-content/50 uppercase">
              <th>Job</th>
              <th>State</th>
              {showProject ? <th>Where</th> : null}
              <th className="max-lg:hidden">Actor</th>
              <th>Started</th>
              <th className="max-lg:hidden">Duration</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => (
              <tr key={j.id} className="transition-colors duration-150 hover:bg-base-200/50">
                <td className="max-w-md min-w-56">
                  <Link to={`/jobs/${j.id}`} className="font-semibold break-anywhere hover:underline">
                    {j.summary}
                  </Link>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-base-content/55">
                    <span className="badge badge-ghost badge-xs">{j.kind}</span>
                    {j.branch ? (
                      <span className="font-mono">
                        {j.branch}
                        {j.commitSha ? `@${shortSha(j.commitSha)}` : ''}
                      </span>
                    ) : null}
                  </div>
                </td>
                <td>
                  <div className="flex flex-col items-start gap-1">
                    <StatusBadge kind="job" value={j.state} />
                    {j.stage && isActive(j) ? <span className="text-xs text-base-content/55">{j.stage}</span> : null}
                  </div>
                </td>
                {showProject ? (
                  <td className="text-sm">
                    <Where j={j} />
                    {j.vps ? <div className="text-xs text-base-content/55">on {j.vps.name}</div> : null}
                  </td>
                ) : null}
                <td className="text-sm max-lg:hidden">
                  <div className="flex items-center gap-1.5">
                    <span className="whitespace-nowrap">{j.actor.name || j.actor.username}</span>
                    <SourceBadge source={j.source} />
                  </div>
                </td>
                <td className="text-sm whitespace-nowrap text-base-content/65">
                  <RelativeTime value={j.createdAt} />
                </td>
                <td className="text-sm whitespace-nowrap text-base-content/65 tabular-nums max-lg:hidden">{formatDuration(durationBetween(j.createdAt, j.finishedAt, now))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
